import React from "react";
import {useTranslation} from "react-i18next";
import {QueueStatus} from "../utils/waitingRoom";

/**
 * 入場制御（待合室）の画面。 issue #2992 / 報告書 §8 No.11
 * 構成・文言は `docs/data/2992-waiting-room-mock.html` に合わせている。
 *
 * ■ 3 つの状態
 *   ① 待機中     … 前の待ち人数と目安時間。順番が来ると自動で切り替わる
 *   ② 順番到来   … 10 秒カウントダウン ＋「予約ページへ進む」ボタン
 *                   （バックグラウンドタブでは自動遷移が遅れるためボタンも要る）
 *   ③ 確認失敗   … 整理番号は端末に保持しているので**順番は失われない**。
 *                   自動で再確認しつつ、手動の「今すぐ再確認する」も出す
 *
 * ■ ★「再読み込みしても順番は変わりません」
 *   整理番号は localStorage と Cookie に持ち、サーバーも再発番しない。
 *   ここを利用者に伝えないと、不安から何度もリロードして待合室自体の負荷が増える。
 */

type Props = {
  /** 直近の状態確認の結果（失敗直後は前回値が残る） */
  status?: QueueStatus;
  /** 状態確認に失敗しているか */
  isError: boolean;
  /** 次の自動確認までの秒数 */
  nextCheckSec: number;
  /** 順番到来後、自動遷移までの秒数 */
  admitCountdownSec: number;
  /** 最終更新時刻の表示用 */
  updatedAt?: Date;
  /** 「今すぐ再確認する」 */
  onRetry: () => void;
  /** 「予約ページへ進む」 */
  onEnter: () => void;
};

const two = (n: number) => String(n).padStart(2, "0");
const clockOf = (d?: Date) => (d ? `${two(d.getHours())}:${two(d.getMinutes())}:${two(d.getSeconds())}` : "--:--:--");

/**
 * 追加メッセージを表示中の言語で選ぶ。無ければ日本語へフォールバック。
 * ★`message.ja` 固定だと、英語・繁体字の利用者に日本語が出る（§2-I）。
 */
const localizedMessage = (
  message: QueueStatus["message"] | undefined,
  language: string,
): string | undefined => {
  if (!message) {
    return undefined;
  }
  const key = language.startsWith("en") ? "en" : language.startsWith("zh") || language === "tw" ? "zh" : "ja";
  return message[key] || message.ja;
};

const WaitingRoom: React.FC<Props> = ({
  status,
  isError,
  nextCheckSec,
  admitCountdownSec,
  updatedAt,
  onRetry,
  onEnter,
}) => {
  const {t, i18n} = useTranslation();
  const admitted = status?.admitted === true;
  const position = status?.position ?? 0;
  const etaMin = status?.etaSec !== undefined ? Math.max(1, Math.ceil(status.etaSec / 60)) : undefined;

  return (
    // ★本文は白の角丸カードで囲む。ページ背景（`.l-page-content` の #E6F5F6）に文字が直接乗ると読みづらいため。
    //   `box-white` は既存の共通クラス（角丸 5px・白背景・文字色 #455873）。
    //   `waiting-room` はこのカード専用（`scss/component/box.scss`）。先頭・末尾の要素の
    //   外側マージンを消して**上下の余白を padding に揃える**。`.heading-2` の
    //   margin-top（40px / PC 56px）が残ると上だけ広くなるため。
    //   ★共通クラス側は変更していないので、通常時の画面には影響しない。
    <div className="wrap-content-900 pt-40 pb-40">
      <div className="box-white large ta-c waiting-room">
      <h1 className="heading-2 mb-24">
        {admitted ? t("お待たせしました") : t("順番にご案内しています")}
      </h1>

      {/* ② 順番到来 */}
      {admitted && (
        <>
          <p className="fz-16 fw-b mb-24">{t("順番になりました。予約ページへお進みください。")}</p>
          <p className="fz-14 mb-16">
            {t("{{sec}}秒後に自動的に予約ページへ移動します。", {sec: admitCountdownSec})}
          </p>
          <button type="button" className="button-medium-orange padding-large" onClick={onEnter}>
            {t("予約ページへ進む")}
          </button>
          <p className="fz-12 mt-24 c-light_gray">
            {t("予約ページへ移動後、30分間操作がない場合はご案内を終了します。その場合は、再度お並びください。")}
          </p>
        </>
      )}

      {/* ③ 確認失敗（順番は保持されている） */}
      {!admitted && isError && (
        <>
          <p className="fz-16 fw-b mb-10">{t("順番の確認に時間がかかっています。")}</p>
          <p className="fz-14 mb-24">{t("順番は保持されています。自動で再確認しますので、このままお待ちください。")}</p>
          <p className="fz-14 mb-8">
            {t("最後に確認した順番")}：<span className="fz-24 fw-b">{position.toLocaleString()}</span>
            {t("人")}（{clockOf(updatedAt)}）
          </p>
          <button type="button" className="button-medium-orange padding-large mb-16" onClick={onRetry}>
            {t("今すぐ再確認する")}
          </button>
          <p className="fz-12 c-light_gray">
            {t("次回の自動確認まで{{sec}}秒", {sec: nextCheckSec})}
          </p>
        </>
      )}

      {/* ① 待機中 */}
      {!admitted && !isError && (
        <>
          <p className="fz-16 fw-b mb-10">{t("ただいま大変混み合っています。")}</p>
          <p className="fz-14 mb-24">{t("順番が来ましたら、自動的に予約ページへ移動します。")}</p>
          <p className="fz-14 mb-4">{t("あなたの前の待ち人数")}</p>
          <p className="fz-28 fw-b c-red mb-16">
            {position.toLocaleString()}<span className="fz-16">{t("人")}</span>
          </p>
          {etaMin !== undefined ? (
            <p className="fz-14 mb-24">{t("お待ち時間の目安：約{{min}}分", {min: etaMin})}</p>
          ) : (
            // ★目安が読めない局面（同時滞在の安全弁で足止め中）は、あえて数字を出さない。
            //   誤った目安を出すと、過ぎても入れないときに不信を招く
            <p className="fz-14 mb-24">{t("しばらくお待ちください")}</p>
          )}
          <p className="fz-16 c-light_gray">{t("このページを閉じずに、そのままお待ちください。")}</p>
          <p className="fz-16 c-light_gray">{t("ページを再読み込みしても、順番は変わりません。")}</p>
          <p className="fz-12 c-light_gray mt-16">
            {t("最終更新")} {clockOf(updatedAt)}（{t("自動で更新されます")}）
          </p>
        </>
      )}

      {/* 管理者が設定ドキュメントに書いた追加メッセージ。★表示中の言語で出す（§2-I） */}
      {localizedMessage(status?.message, i18n.language) && (
        <p className="fz-14 mt-24">{localizedMessage(status?.message, i18n.language)}</p>
      )}
      </div>
    </div>
  );
};

export default WaitingRoom;
